import { OrderFill } from 'widgets/fillsGraph/fillsGraphData';
import { BucketedData } from 'widgets/fillsGraph/fillsGraphDataBuckets';
import { computeGroupedData } from 'utils/components/TimeSeriesChart/groupedChartData';
import { computeBucketBarWidthPercentage } from 'utils/d3fc/chartSeries';
import { GroupedSeries } from '@d3fc/d3fc-group';

export interface FillsGraphExpectedVsActualChartDataItem {
    time: number;
    expectedVolume: number;
    actualVolume: number;
    barWidthPercent: number;
}

export interface FillsGraphExpectedVsActualData {
    bucketedData: BucketedData[];
    chartData: GroupedSeries<
        Partial<FillsGraphExpectedVsActualChartDataItem>
    >[];
    lastBucketExpectedVolume?: number;
    lastBucketActualVolume?: number;
    lastBucketAboveThreshold: boolean;
    chartMax: number;
}

export function buildExpectedVsActualChartData(
    data: OrderFill[],
    bucketSizeInMs: number,
    autoHideLastBucket: boolean,
    autoHideLastBucketThresholdPercent: number,
    showAutoHiddenLastBucket?: boolean
): FillsGraphExpectedVsActualData {
    const bucketedData: BucketedData[] = data
        .filter((d) => !!d.bucket)
        .map((d) => d.bucket!);

    let lastBucketExpectedVolume: number | undefined = undefined;
    let lastBucketActualVolume: number | undefined = undefined;
    let lastBucketAboveThreshold = false;

    const bucketsMax = (b: BucketedData[]) =>
        Math.max(...b.map((p) => Math.max(p.actualVolume, p.expectedVolume)));

    let chartMax = bucketsMax(bucketedData);

    if (bucketedData.length > 1) {
        const last = bucketedData[bucketedData.length - 1];
        lastBucketActualVolume = last.actualVolume;
        lastBucketExpectedVolume = last.expectedVolume;

        if (autoHideLastBucket) {
            const previous = bucketedData.slice(0, bucketedData.length - 1);
            const previousMax = Math.max(
                ...previous.map((p) =>
                    Math.max(p.actualVolume, p.expectedVolume)
                )
            );

            const lastMax = Math.max(last.actualVolume, last.expectedVolume);

            const prevThreshold =
                previousMax +
                (previousMax * autoHideLastBucketThresholdPercent) / 100;
            if (lastMax > prevThreshold) {
                lastBucketAboveThreshold = true;

                if (!showAutoHiddenLastBucket) {
                    bucketedData.splice(bucketedData.length - 1, 1);
                    chartMax = previousMax;
                }
            }
        }
    }

    const chartData = computeGroupedData(
        bucketedData.map((x) => ({ ...x, time: x.midTime })),
        (b: BucketedData) => ({
            time: b.midTime,
            expectedVolume: b.expectedVolume,
            actualVolume: b.actualVolume,
            barWidthPercent: computeBucketBarWidthPercentage(
                b.startTime,
                b.endTime,
                bucketSizeInMs
            ),
        }),
        ['expectedVolume', 'actualVolume']
    );

    return {
        chartData,
        chartMax,
        bucketedData,
        lastBucketExpectedVolume,
        lastBucketActualVolume,
        lastBucketAboveThreshold,
    };
}
